/**
 * @module app.ext.epmp
 */

import { autoinject } from 'aurelia-framework';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { MsalService } from 'app/shared/services/msal-service';
import * as CD from 'app/cd/models/essette-api';

@autoinject
export class EpmpManager {

    ////****************** Fields and Properties ************************************////
    private idType: string;
    private sourceUrl: string;
    private configUrlContacts: string;
    private configUrlPreferences: string;
    private userIdKey: string;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly logger: AppLogger,
        private readonly env: EnvironmentService,
        private readonly msalService: MsalService) {
        this.logger.source = this;
        this.idType = String(this.env.config.externalSystems.epmp?.idType);
        this.sourceUrl = String(this.env.config.externalSystems.epmp?.sourceUrl);
        this.configUrlContacts = String(this.env.config.externalSystems.epmp?.configUrlContacts);
        this.configUrlPreferences = String(this.env.config.externalSystems.epmp?.configUrlPreferences);
        this.userIdKey = String(this.env.config.externalSystems.epmp?.userIdKey);
    }

    ////****************** Events ***************************************************////

    ////****************** Functions ************************************************////
    public async open(memberIdentifier: string, groupNumber: string, variation: 'Contacts' | 'Preferences'): Promise<void> {
        let configUrl = '';
        const token = await this.msalService.getAccessToken();
        const userData = {
            idType: this.idType,
            idValue: this.formatMemberIdentifier(memberIdentifier),
            policyNumber: groupNumber,
            userId: this.msalService.getClaimFromAccount(this.userIdKey)
        };

        switch(variation) {
            case 'Contacts':
                configUrl = this.configUrlContacts;
                break;
            case 'Preferences':
                configUrl = this.configUrlPreferences;
                break;
            default:
                this.logger.warn(`Unknown variation: ${variation}`);
                return;
        };

        const popupName = 'epmpPopup_' + Date.now();
        const w = window.open('app/ext/epmp/epmp-popup.html', popupName, 'top=25, left=' + (screen.width / 4) + ',width=1000,height=' + (screen.height - 150));
        if(!w) {
            this.logger.warn('Unable to open external epmp pop-up');
            return;
        }

        // Set parameters directly on the popup window (IQC pattern)
        (<any>w)['Essette.EPMP.sourceUrl'] = this.sourceUrl;
        (<any>w)['Essette.EPMP.configUrl'] = configUrl;
        (<any>w)['Essette.EPMP.token'] = token;
        (<any>w)['Essette.EPMP.userData'] = userData;
        (<any>w)['Essette.EPMP.variation'] = variation;
    }

    public getValidGroupNumber(memberIdentifier: string, memberEligibility: CD.MemberEligibility[]): string | null {
        if(!memberIdentifier || !memberEligibility) return null;

        //Requirement by EPMP that only member identifiers ending with '01' are valid
        const validMemberIdentifier = memberIdentifier.endsWith('01');

        //Ensures the memberplan has an eligibility with a valid group number as specified within the config
        const memberGroupNumbers = memberEligibility.map(e => e.groupNumber!) || [];
        const validGroupNumbers = this.env.config.externalSystems?.epmp?.validGroupNumbers || [];

        const validMemberGroupNumber = memberGroupNumbers.find(groupNumber => validGroupNumbers.includes(groupNumber)) || null;

        return validMemberIdentifier ? validMemberGroupNumber : null;
    }

    private formatMemberIdentifier(memberIdentifier: string): string {
        if(!memberIdentifier || memberIdentifier.length < 3) return memberIdentifier;
        //adds a "-" before the last two characters
        const prefix = memberIdentifier.slice(0, -2);
        const suffix = memberIdentifier.slice(-2);
        return `${prefix}-${suffix}`;
    }
    ////****************** Service Calls ********************************************////

}
